import React, { useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
// Material UI
import { Grid, Typography, Icon } from '@mui/material';
import { InputSearch, Button } from 'Components';
import Checkbox from 'Components/Checkbox';
import useExport from 'Components/Table/Export';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import TableView from 'Components/TableView';
import { MenuComponent } from 'Components/Menu';
import moment from 'moment';
import { Tooltip } from 'Components';

import SpeedOverrideCreateEdit from '../speedOverrideCreateEdit';
import SpeedOverrideDelete from '../speedOverrideDelete';
import { getRouteColorFromSpeed } from 'Components/Map/utils';
import { TypeLayer, TypeObjectMap } from 'Components/Map/providers/enums';
import {Timezone} from 'Components/uiControls';

import './style.scss'

// International
import { useIntl } from "react-intl";

import {
    mapSpeedOverrideSearch,
    mapSpeedOverrideOpenEditCreateModal,
    mapSpeedOverrideOpenDeleteModal,
    drawGetDirections,
    selectCreateClickMapType,
    addPointers,
    setLatLngRestart,
    clearRedux,
    setMapRoutesOptionsRestart
} from 'Redux/actions';

const ROWS_PER_PAGE = 25;

const SpeedOverrideList = (props) => {

    const { messages, locale } = useIntl();

    const speedOverrideActions = [
        messages['edit'],
        messages['delete']
    ];

    const { mapRef } = props
    const [searchText, setSearchText] = useState("");
    const [offset, setOffset] = useState(0 * ROWS_PER_PAGE);
    const [data, setData] = useState([]);
    const [selectedRows, setSelectedRows] = useState([])
    const [page, setPage] = useState(0);
    const [total, setTotal] = useState(20);
    const [itemSelected, setItemSelected] = useState(0)

    const dispatch = useDispatch();

    const timeZone = useSelector((state) => state.securityRedux.user.timeZone);
    const isKph = useSelector((state) => state.securityRedux.user.isKph);
    const speedOverrideData = useSelector(state => state.realtimeMapSpeedOverrideListRedux.speedOverrideData);
    const loadingSpeedOverrideData = useSelector(state => state.realtimeMapSpeedOverrideListRedux.loadingSpeedOverrideData);
    const openDelete = useSelector(state => state.realtimeMapSpeedOverrideDeleteRedux.open);
    const openCreate = useSelector(state => state.realtimeMapSpeedOverrideCreateEditRedux.open);

    useEffect(()=> {
        setData([]);

        return () => {
            dispatch(setMapRoutesOptionsRestart())
            setData([]);
            //remove layer route on map
            mapRef?.current?.removeLayer(TypeLayer.RouteLayer);
            dispatch(clearRedux('MAP_SPEED_OVERRIDE', { error: false, response: null }));
        }
    },[])

    useEffect(()=> {
        if(!loadingSpeedOverrideData){
            const { items, total, restart } = speedOverrideData;
            if (!restart) {
                const copyData = [...data];
                for (const item of items) {
                    // check the object in data
                    const existingItem = copyData.find(i => i.id === item.id);
                    if (!existingItem) {
                      // No exist element in data
                      copyData.push(item);
                    }
                }
                setData(copyData)
                setTotal(total);
      
            } else {
                setData(items);
                setSelectedRows([])
            }
        }
    }, [speedOverrideData])

    useEffect(() => {
        dispatch(drawGetDirections(false));
        dispatch(addPointers([]));
        dispatch(selectCreateClickMapType(TypeObjectMap.Default))
        dispatch(setLatLngRestart())

        dispatch(mapSpeedOverrideOpenEditCreateModal(false, null))
        dispatch(clearRedux('MAP_SPEED_OVERRIDE_CREATE_EDIT'));
        dispatch(mapSpeedOverrideOpenDeleteModal(false, []));
        dispatch(clearRedux('MAP_SPEED_OVERRIDE_DELETE'));
    }, [locale]);

    const onSearch = useCallback((value) => {
        setSearchText(value);

        if (window.typingTime) {
            clearTimeout(window.typingTime);
        }

        window.typingTime = setTimeout(() => {
            setData([]);
            setOffset(0);
            //search the data
            dispatch(mapSpeedOverrideSearch(value, ROWS_PER_PAGE, 0))

            window.typingTime = null;
        }, 1000);
    });

    const onChangePage = useCallback((paramPage) => {
        if (loadingSpeedOverrideData) return //prevents multiple requests
        const newOffset = (paramPage - 1) * ROWS_PER_PAGE;
        dispatch(mapSpeedOverrideSearch(searchText, ROWS_PER_PAGE, newOffset))
        setOffset(newOffset);
        setPage(paramPage)
    });

    const onSelectCard = (row) => {
        if (row?.id == itemSelected) {
            //clear
            setItemSelected(0)
            //remove layer route on map
            mapRef?.current?.removeLayer(TypeLayer.RouteLayer);
            return
        }
        setItemSelected(row?.id || 0);
        //startAddress
        //endAddress
        if(row?.encodedPath && row?.startAddress && row?.endAddress){
            let valueColor = getRouteColorFromSpeed(Math.round(row?.speedLimit));
            const startAddress =  JSON.parse(row?.startAddress)
            const endAddress =  JSON.parse(row?.endAddress)
            mapRef.current.drawPolygonWithEncodedPath(
                row?.encodedPath, 
                valueColor?.color, 
                { lat: startAddress.latitude, lng: startAddress.longitude },
                { lat: endAddress.latitude, lng: endAddress.longitude }, 
            );
        }
        
    }

    return (
        <div>
            <Grid
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="flex-start"
            >
                <Grid item xs>
                    <InputSearch
                        className="speed-override-search"
                        placeholder={messages['search']}
                        searchText={searchText}
                        onSearch={onSearch}
                    />
                </Grid>
                <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                >
                    <Grid item xs={7}>
                        <Button
                            type="icon"
                            icon="add_box"
                            tooltip={messages['add']}
                            onClick={() => {
                                dispatch(mapSpeedOverrideOpenEditCreateModal(true, null));
                            }}
                        />

                        {
                            selectedRows?.length > 0 &&
                            <Button
                                type="icon"
                                icon="delete"
                                tooltip={messages['delete']}
                                disabled={!selectedRows.length}
                                onClick={() => {
                                    dispatch(mapSpeedOverrideOpenDeleteModal(true, selectedRows));
                                }}
                            />
                        }
                    </Grid>
                    <Grid item xs>
                        <Checkbox
                            id={'-selectAll'}
                            //style={{ paddingTop: 5 }}
                            size="md"
                            value={data?.length === selectedRows.length}
                            label={messages['map_landmark_select_all']}
                            className='speed-override-selectall'
                            onChange={(checked) => {
                                if (checked) {
                                    setSelectedRows(data);
                                } else {
                                    setSelectedRows([]);
                                }
                            }}
                        ></Checkbox>
                    </Grid>
                </Grid>
                <Grid
                    container
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                >
                    <TableView
                        data={data}
                        loading={loadingSpeedOverrideData}
                        page={page}
                        rowsPerPage={25}
                        name="speed-override-list"
                        topSpaceSize={242}
                        infiniteScroll={true}
                        viewType="list"
                        messages={messages}
                        itemSelector={false}
                        serverSide
                        onChangePage={onChangePage}
                        total={total}
                        moreRowsAvailable={data.length < total}
                        renderRow={(row) => {
                            return (
                                <div style={{ padding: '10px 15px 10px 2px' }}>
                                    <div className={row?.id == itemSelected ? "speed-override-card speed-override-container-selected" : "speed-override-card speed-override-container"}>
                                        <Grid
                                            container
                                            direction="row"
                                            justifyContent="flex-start"
                                            alignItems="center"
                                        >
                                            <Grid item>
                                                <Checkbox
                                                    id={'checked_' + row?.id}
                                                    value={selectedRows.find((item) => item?.id == row?.id) ? true : false}
                                                    label={''}
                                                    //size={'sm'}
                                                    className="speed-override-checkbox"
                                                    onChange={() => {
                                                        let newSelectedRows = [ ...selectedRows ];
                                                        if (selectedRows.find((item) => item?.id == row?.id)) {
                                                            newSelectedRows = selectedRows.filter((item) => item?.id!= row?.id);
                                                        } else {
                                                            newSelectedRows.push(row);
                                                        }

                                                        setSelectedRows(newSelectedRows);
                                                    }}
                                                ></Checkbox>
                                            </Grid>
                                            <Grid item xs zeroMinWidth>
                                                <Typography
                                                    noWrap
                                                    variant="subtitle1"
                                                    className="speed-override-title"
                                                    onClick={() => onSelectCard(row)}
                                                >
                                                    {row?.name}
                                                </Typography >
                                            </Grid>
                                        </Grid>

                                        <Grid
                                            container
                                            wrap="nowrap"
                                            direction="column"
                                            justifyContent="flex-start"
                                            alignItems="flex-start"
                                            onClick={() => onSelectCard(row)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <Grid item xs zeroMinWidth>
                                                <Typography className="speed-override-subtitle-main" noWrap variant="subtitle2" color={row?.id == itemSelected ? 'inherit' : ''}>
                                                    {messages['speedOverride_correctedSpeed']}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs zeroMinWidth>
                                                <Typography className="speed-override-subtitle" noWrap variant="subtitle2" color={row?.id == itemSelected ? 'inherit' : ''}>
                                                    {`${isKph ? Math.round(row?.speedLimit * 1.60934) : Math.round(row?.speedLimit)} ${isKph ? 'kph' : 'mph'}`}
                                                </Typography>
                                            </Grid>
                                        </Grid>

                                        <Grid
                                            container
                                            wrap="nowrap"
                                            direction="column"
                                            justifyContent="flex-start"
                                            alignItems="flex-start"
                                            onClick={() => onSelectCard(row)}
                                            style={{ cursor: 'pointer', overflow: 'hidden' }}
                                        >
                                            <Grid item xs zeroMinWidth>
                                                <Typography className="speed-override-subtitle-main" noWrap variant="subtitle2" color={row?.id == itemSelected ? 'inherit' : ''}>
                                                    {messages['speedOverride_lastUpdated']}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs zeroMinWidth>
                                                <div id="timezone-div" style={{ display: "flex" }} className='speed-override-subtitle'>
                                                    <Timezone
                                                        date={new Date(row?.updatedOn)}
                                                        dateFormat={"MMM D"}
                                                        timeFormat="h:mma"
                                                        timezone={timeZone}
                                                        className="timeZoneClassName"
                                                    /> 
                                                    {`${moment(row?.updatedOn).format("MMM D h:mma")} (${row?.userName})`.length > 22 
                                                        ?
                                                        (<Tooltip title={row?.userName} placement={'bottom'}>
                                                            <label style={{ paddingLeft: 5 }} className="timeZoneClassName">{` (${row?.userName})`}</label>
                                                        </Tooltip>)
                                                        :
                                                        (<label style={{ paddingLeft: 5 }} className="timeZoneClassName">{` (${row?.userName})`}</label>)
                                                    }
                                                </div>
                                            </Grid>
                                        </Grid>

                                        <MenuComponent
                                            disabled={loadingSpeedOverrideData}
                                            options={speedOverrideActions}
                                            icon={'more_vert'}
                                            className="speed-override-actions icon-menu-vehicle-service showInHover"
                                            onClick={(type) => {
                                                if (type === messages['edit']) {
                                                    dispatch(mapSpeedOverrideOpenEditCreateModal(true, row?.id))
                                                } else if (type === messages['delete']) {
                                                    dispatch(mapSpeedOverrideOpenDeleteModal(true, [row]))
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                            )
                        }}>
                    </TableView>
                </Grid>
            </Grid>

            { 
                openCreate && <SpeedOverrideCreateEdit mapRef={mapRef} /> 
            }

            {
                openDelete && <SpeedOverrideDelete mapRef={mapRef} />
            }
        </div>
    )

}

export default SpeedOverrideList;